import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IonNav, IonicModule, MenuController, ModalController, NavParams } from '@ionic/angular';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { ApiResponseModel } from 'src/app/core/services/api/api-response.model';
import { DataService } from 'src/app/core/services/api/data.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { UiUtilityService } from 'src/app/core/services/ui-utility.service';
import { DashboardService } from 'src/app/features/dashboard/services/dashboard.service';
import { gracePeriodOptions } from 'src/app/shared/config/grace-period.constants';
import {
  defaultTempUnit,
  tempUnit,
  tempUnitIds,
} from 'src/app/shared/config/temp-unit.constant';
import { EndPointsEnum } from 'src/app/shared/enums/end-points';
import { HttpRequestTypeEnum } from 'src/app/shared/enums/http-request-type.enum';
import { AlertButtonModel } from 'src/app/shared/models/alertButtonModel';

import {
  WearerDetailsModel,
  WearerGroup,
} from 'src/app/shared/models/wearer.model';
import { environment } from 'src/environments/environment';
import { WearerService } from '../../services/wearer.service';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputErrorComponent } from 'src/app/shared/components/input-error/input-error.component';
import { wearerDetailsInputConfig } from './wearer-details-input.config';
import { UnifiedInput } from 'src/app/shared/components/unified-custom-input/unified-input.types';
import { UnifiedCustomInputComponent } from 'src/app/shared/components/unified-custom-input/unified-custom-input.component';
import { cloneDeep, set } from 'lodash-es';
import { BandAssociationService } from 'src/app/features/band/services/band-association.service';
import { FACILITY_ID_URL, TOAST } from 'src/app/shared/config/app.constant';
import { BandEnum, BandPageFlowEnum } from 'src/app/shared/enums/band.enum';
import { SelectBedComponent } from 'src/app/features/bed-management/pages/select-bed/select-bed.component';
import { SelectRoomPage } from '../select-room/select-room.page';
import { BedDetailsPage } from 'src/app/features/bed-management/pages/bed-details/bed-details.page';
import { RoomDetailsPage } from 'src/app/features/settings/room-management/room-details/room-details.page';
import { AssignDevicePage } from 'src/app/features/band/pages/assign-device/assign-device.page';
import { BandCompletionPage } from 'src/app/features/band/pages/band-completion/band-completion.page';
import { UnifiedHeaderComponent } from 'src/app/shared/components/unified-header/unified-header.component';
import { Capacitor } from '@capacitor/core';
import { AccessControlDirective } from 'src/app/shared/directives/access-control.directive';
import { InputEnum } from 'src/app/shared/enums/input-field.enum';
import { SettingsService } from 'src/app/features/settings/services/settings.service';
import { UnifiedSelectComponent } from 'src/app/shared/components/unified-select/unified-select.component';
import { DeviceDetailsPage } from 'src/app/features/device-management/device-details/device-details.page';
@Component({
  standalone: true,
  selector: 'app-wearer-details',
  templateUrl: './wearer-details.page.html',
  styleUrls: ['./wearer-details.page.scss'],
  imports: [
    CommonModule,
    IonicModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    InputErrorComponent,
    UnifiedCustomInputComponent,
    UnifiedHeaderComponent,
    AccessControlDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WearerDetailsPage implements OnInit, OnDestroy {
  wearerId: string = '';
  isRootPage: boolean = false;
  destroyRef = inject(DestroyRef);
  wearerDetails: WearerDetailsModel | null = null;
  wearerDetailsForm: FormGroup | null = null;
  isSaveDisabled = true;
  isEditEnable = false;
  gracePeriodOptions = gracePeriodOptions;
  alertThresholdOption: Map<number, number> | null = null;
  subscriptions: Subscription = new Subscription();

  tempUnitId = defaultTempUnit;
  tempUnit = tempUnit;
  tempUnitIds = tempUnitIds;
  isAsset: boolean = false;
  storedGroups: WearerGroup[] = [];
  roomSwitchTriggered: boolean = false;
  wearerDetailsInputConfig: UnifiedInput[] = cloneDeep(
    wearerDetailsInputConfig
  );
  roomDisabled = false;
  enableShowAlert = false;
  wearerStatus = [
    { name: 'Active', value: false },
    { name: 'Inactive', value: true },
  ];
  showRoom: boolean = false;
  backButtonClicked: boolean = false;
  isNative = Capacitor.isNativePlatform();
  isRoomSummaryEnabled = true;
  isBedSummaryEnabled = true;
  wearerAlerts: any[] = [];
  isPCCEnabled = false;
  isBedSlotEnabled = false;
  rooms: any[] = [];
  select: any = {};
  isCasparEnabled = false;
  isDeviceManagementEnabled = false;
  isEtectRxEnabled = false;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private wearerService: WearerService,
    private formBuilder: FormBuilder,
    private dataService: DataService<WearerDetailsModel>,
    private uiUtilityService: UiUtilityService,
    private bandService: BandAssociationService,
    private authService: AuthService,
    private headerService: HeaderService,
    private dashboardService: DashboardService,
    private cdRef: ChangeDetectorRef,
    private navParams: NavParams,
    private nav: IonNav,
    private menuCtrl: MenuController,
    private settingsService: SettingsService,
    private modalCtrl: ModalController
  ) {
  }

  getSingularFormWearerGroup(groupName: string) {
    return this.dashboardService.getSingularFormWearerGroup(groupName);
  }

  checkIfAsset() {
    this.isAsset = this.wearerDetailsForm?.get('wearerGroupId')?.value === '3';
  }

  ngOnInit(): void {
    this.wearerId = this.navParams.get('id') || this.wearerId;
    this.isSaveDisabled = true;
    this.isEditEnable = this.navParams.get('mode') === 'edit' ? true : false;
    this.isRoomSummaryEnabled = this.authService.isFeatureEnabled('getRoomDetail');
    this.isBedSummaryEnabled = this.authService.isFeatureEnabled('getBedDetail');
    this.isDeviceManagementEnabled = this.authService.isFeatureEnabled('getDeviceList');
    this.isPCCEnabled = this.headerService.isPCCEnabled;
    this.isBedSlotEnabled = this.headerService.isBedSlotEnabled;
    this.isCasparEnabled = this.headerService.isCasparEnabled;
    this.isEtectRxEnabled = this.headerService.isEtectRxEnabled;
  }

  ionViewWillEnter() {
    const loginData = this.authService.getLoginData;
    this.wearerAlerts = loginData.wearerAlertsMetadata;
    if (loginData.tempUnit) {
      this.tempUnitId = loginData.tempUnit;
    }
    this.checkForAddWearer().then((wearer) => {
      // wearer['etectDetails'] = {
      //   etectId: 3,
      //   etectMonitoringStatus: true,
      //   etectAlertStatus: true
      // };
      this.wearerDetails = wearer;
      if (this.wearerDetails.residentDetail.isDeleted == false) {
        this.roomDisabled = false;
      } else {
        this.roomDisabled = true;
      }
      this.createForm();
      this.checkIfAsset();
      this.wearerDetailsForm?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.checkIfAsset();
          if (this.wearerDetailsForm) {
            this.isSaveDisabled =
              this.wearerDetailsForm.invalid || !this.wearerDetailsForm.dirty;
            if (
              this.wearerDetailsForm.value.firstName ||
              this.wearerDetailsForm.value.lastName ||
              this.wearerDetailsForm.value.localId
            ) {
              this.isSaveDisabled = false;
            } else {
              this.isSaveDisabled = true;
            }
          }
        });
    });
  }

  async checkForAddWearer(): Promise<WearerDetailsModel> {
    const facilityId = this.headerService.getFacilityId();
    const promise: Promise<WearerDetailsModel> = new Promise((resolve) => {
      if (!this.wearerId) {
        this.wearerService.getWearerGroups(facilityId).subscribe((response) => {
          if (response.success && response.data) {
            this.storedGroups = response.data;
            this.wearerDetailsInputConfig.map((config) => {
              config.detail = true;
            });
          }
          resolve(new WearerDetailsModel());
        });
      } else {
        forkJoin({
          wearer: this.wearerService.getWearerDetails(this.wearerId),
          wearerGroups: this.wearerService.getWearerGroups(facilityId),
        }).subscribe(
          (response: {
            wearer: ApiResponseModel<WearerDetailsModel>;
            wearerGroups: any;
          }) => {
            this.storedGroups = response.wearerGroups.data as any[];
            if (response.wearer.success) {
              this.wearerDetailsInputConfig.map((config) => {
                config.detail = this.isEditEnable;
                if (response.wearer.data && config.name) {
                  if (response.wearer.data.rooms) {
                    this.showRoom = true;
                  } else {
                    this.showRoom = false;
                  }
                  config.value = (response.wearer.data.residentDetail as any)[
                    config.name
                  ];
                }
              });
              resolve(response.wearer.data || new WearerDetailsModel());
            } else {
              resolve(new WearerDetailsModel());
            }
          }
        );
      }
    });
    return promise;
  }

  createForm() {
    let temp: any = {};
    if (this.wearerAlerts?.length) {
      for (const alert of this.wearerAlerts) {
        temp[`alertSettings.${alert.key}`] = [this.wearerDetails?.residentDetail?.alertSettings ? (this.wearerDetails?.residentDetail?.alertSettings?.[alert.key] || false) : alert.default]
        for (const alert1 of alert.alerts) {
          temp[`alertSettings.${alert1.key}`] = [{ value: this.wearerDetails?.residentDetail?.alertSettings?.[alert1.key] || alert1.default, disabled: this.isReadMode }]
        }
      }
    }
    if (this.isEtectRxEnabled) {
       const etectDetails = this.wearerDetails?.etectDetails;

       const monitoringVal = etectDetails?.etectMonitoringStatus as any;
       const alertVal = etectDetails?.etectAlertStatus as any;

       // ON only if monitoring status is explicitly 'enabled'or alert status is resumed  or true
       
       const isMonitoring = monitoringVal === 'enabled' || monitoringVal === true;
       temp['etectDetails.etectMonitoringStatus'] = [isMonitoring];

       const isAlertOn = alertVal === 'resumed' || alertVal === true;
       temp['etectDetails.etectAlertStatus'] = [isAlertOn];

       temp['etectDetails.etectId'] = [etectDetails?.etectId ?? null];

    }
    this.wearerDetailsForm = this.formBuilder.group({
      firstName: [
        this.wearerDetails?.residentDetail.firstName,
        [Validators.required],
      ],
      lastName: [
        this.wearerDetails?.residentDetail.lastName,
        [Validators.required],
      ],
      localId: [this.wearerDetails?.residentDetail.localId],
      wearerGroupId: [
        {
          value: this.wearerId
            ? this.wearerDetails?.residentDetail.wearerGroupId
            : this.isPCCEnabled ? this.storedGroups[1]?.id : this.storedGroups[0]?.id,
          disabled: this.wearerId || this.isPCCEnabled,
        },
        [Validators.required],
      ],
      wearerStatus: [
        {
          value: this.wearerId
            ? this.wearerDetails?.residentDetail.isDeleted
            : false,
          disabled: this.isReadMode,
        },
        [Validators.required],
      ],
      slotName: [this.wearerId ? this.wearerDetails?.residentDetail?.slotName : ''],
      roomId: [this.wearerId && this.wearerDetails?.rooms?.[0]?.id ? this.wearerDetails?.rooms?.[0]?.id : ''],
      roomName: [this.wearerId && this.wearerDetails?.rooms?.[0]?.roomName ? this.wearerDetails?.rooms?.[0]?.roomName : ''],
      ...temp
    });
    this.cdRef.detectChanges();
  }

  inputChanged(inputConfig: UnifiedInput) {
    if (this.wearerDetailsForm && inputConfig.name) {
      this.wearerDetailsInputConfig.map((config) => {
        if (config.name === inputConfig.name) {
          config.value = inputConfig.value;
        }
      });
      this.wearerDetailsForm.markAsDirty();
      this.wearerDetailsForm.controls[inputConfig.name].patchValue(
        inputConfig.value
      );
      this.cdRef.detectChanges();
    }
  }

  getControl(controlName: string): AbstractControl | null {
    return this.wearerDetailsForm && this.wearerDetailsForm.get(controlName);
  }

  changeStatus(event: any) {
    if (this.wearerDetails?.residentDetail.isDeleted == true) {
      this.enableShowAlert = true;
    } else {
      this.enableShowAlert = false;
    }
    if (event.detail.value == true) {
      this.roomDisabled = true;
    } else {
      this.roomDisabled = false;
      this.enableShowAlert = true;
    }
  }

  get isBandIdVisible() {
    return (
      this.getControl('firstName')?.value && this.getControl('lastName')?.value
    );
  }

  async findBand(device?: any): Promise<void> {
    if (!this.isNative && !device) {
      return;
    }
    const bandId = device?.bandId || device?.tagId;
    this.wearerService.activeWearerDetails.residentDetail.firstName =
      this.getControl('firstName')?.value;
    this.wearerService.activeWearerDetails.residentDetail.lastName =
      this.getControl('lastName')?.value;
    this.wearerService.activeWearerDetails.residentDetail.id = this
      .wearerId as string;
    this.wearerService.activeWearerDetails.rooms =
      this.wearerDetails?.rooms! || [];
    this.wearerService.activeWearerDetails.bands =
      this.wearerDetails?.bands! || [];
    this.bandService.bandFlow = BandEnum.Association;
    let returnBackIndex = 0;
    for (let i = 0; i < 100; i++) {
      const activeNav = await this.nav.getByIndex(i);
      if (activeNav?.element?.localName === 'app-wearer-details') {
        returnBackIndex = i;
        break;
      }
    }
    if (bandId) {
      this.nav.push(BandCompletionPage, { bandId, details: this.wearerDetails, bandInfo: device, returnBackIndex });
    } else {
      this.nav.push(AssignDevicePage, {
        flow: BandPageFlowEnum.WearerAssociation,
        details: this.wearerDetails,
        returnBackIndex,
        isReplaceRemoveEnabled: false
      });
    }
  }

  toggleChanged() {
    if (!this.getControl('location')?.value) {
      this.getControl('locationAlert')?.setValue(false);
      this.getControl('gracePeriod')?.setValue(10);
    }
    if (!this.getControl('locationAlert')?.value) {
      this.getControl('gracePeriod')?.setValue(10);
    }
  }

  async showAlert(header: string, message: string) {
    const buttons: AlertButtonModel[] = [
      {
        text: 'cancel',
        handler: () => {
          this.stopGoingBack();
        },
      },
      {
        text: 'ok',
        handler: () => {
          if (
            !this.isReadMode &&
            !this.roomDisabled &&
            !this.backButtonClicked
          ) {
            this.roomSwitchTriggered = true;
            this.manageWearer('put');
          } else {
            this.navigateBack();
          }
        },
      },
    ];
    this.uiUtilityService.showAlert(message, header, buttons);
  }

  roomSwitch() {
    if (
      this.wearerDetails?.rooms?.[0]?.id == undefined &&
      this.enableShowAlert
    ) {
      this.showAlert(
        'Important',
        'This action will save your previous changes'
      );
    } else {
      this.goToSelectRoomPage();
    }
  }

  navigateToRoomDetails() {
    if (this.wearerDetails?.rooms[0]?.id && this.isRoomSummaryEnabled) {
      this.nav.push(RoomDetailsPage, {
        roomId: this.wearerDetails.rooms[0].id,
      });
    }
  }

  navigateToDeviceManagement(device: any = {}) {
    if (device.macId && this.isDeviceManagementEnabled) {
      this.nav.push(DeviceDetailsPage, { serviceId: device.providerId, macId: device.macId, serviceName: device.providerName, mode: 'detail' });
    }
  }

  selectBed() {
    if (
      this.wearerDetails?.rooms?.[0]?.id == undefined &&
      this.enableShowAlert
    ) {
      this.showAlert(
        'Important',
        'This action will save your previous changes'
      );
    } else {
      const queryObject = {
        roomId: this.wearerDetails?.rooms?.[0]?.id,
        wearerId: this.wearerId,
      };
      this.nav.push(SelectBedComponent, queryObject);
      this.roomSwitchTriggered = false;
    }
  }

  goToSelectRoomPage() {
    let queryObject: any;
    queryObject = {
      roomId: this.wearerDetails?.rooms?.[0]?.id,
      wearerId: this.wearerId,
    };
    this.nav.push(SelectRoomPage, queryObject);
    this.roomSwitchTriggered = false;
  }

  navigateToBedDetails() {
    if (!this.isBedSummaryEnabled) {
      return;
    }
    let bedID = this.wearerDetails?.beds?.[0]?.id;
    if (bedID) {
      this.nav.push(BedDetailsPage, { bedId: bedID, mode: 'detail' });
    }
  }

  dismiss() {
    if (this.uiUtilityService.pageModal) {
      this.dashboardService.refreshDashboardWithData.next(true);
      this.uiUtilityService.pageModal
        .dismiss()
        .then(() => this.menuCtrl.close());
      this.uiUtilityService.pageModal = null;
    }
  }

  onNavBack() {
    if (this.isRootPage) {
      this.dismiss();
    } else {
      this.nav.pop();
    }
  }

  didManageWearer = async (operationType: string) => {
    const facilityId = this.headerService.getFacilityId();
    const wearerFormData = this.wearerService.getManageWearerRequestData(
      this.wearerDetailsForm,
      this.wearerId,
      !this.wearerId
        ? (this.wearerDetails?.residentDetail.facilityId as string)
        : facilityId
    );
    let wearerData: any = {};
    for (const [formkey, formVal] of Object.entries(wearerFormData)) {
      set(wearerData, formkey, formVal);
    }
    const manageWearerUrl = `${environment.apiUrl.residentManagement}${FACILITY_ID_URL}/${EndPointsEnum.manage}`;
    const alertSettings = wearerData.alertSettings;
    if (alertSettings.bedAngle) {
      if (alertSettings.bedAngleCaution === 'NA' && alertSettings.bedAngleWarn === 'NA') {
        this.uiUtilityService.showAlert('Caution and warning bed angle settings cannot both be set to N/A.', 'Alert');
        return;
      } else if (!(isNaN(alertSettings.bedAngleCaution) && isNaN(alertSettings.bedAngleWarn)) && Number(alertSettings.bedAngleCaution) <= Number(alertSettings.bedAngleWarn)) {
        this.uiUtilityService.showAlert('Caution bed angle must be greater than the warning bed angle.', 'Alert');
        return;
      }
    }
    if (alertSettings.bedHeight) {
      if (alertSettings.bedHeightCaution === 'NA' && alertSettings.bedHeightWarning === 'NA') {
        this.uiUtilityService.showAlert('Caution and warning bed height settings cannot both be set to N/A.', 'Alert');
        return;
      } else if (!(isNaN(alertSettings.bedHeightCaution) && isNaN(alertSettings.bedHeightWarning)) && Number(alertSettings.bedHeightCaution) >= Number(alertSettings.bedHeightWarning)) {
        this.uiUtilityService.showAlert('Caution bed height must be lower than the warning bed height.', 'Alert');
        return;
      }
    }
    let manageApiObservable: Observable<any> = new Observable();
    switch (operationType) {
      case HttpRequestTypeEnum.post:
        manageApiObservable = this.dataService.post(
          manageWearerUrl,
          wearerData
        );
        break;
      case HttpRequestTypeEnum.put:
        const roomId = this.select.hasOwnProperty('roomId') ? this.select.roomId : this.wearerDetails?.rooms?.[0]?.id;
        const roomName = this.select.hasOwnProperty('roomName') ? this.select.roomName : this.wearerDetails?.rooms?.[0]?.roomName;
        const payload = { ...wearerData, roomId, roomName, slotName: this.wearerDetails?.residentDetail.slotName }
        manageApiObservable = this.dataService.put(manageWearerUrl, payload);
        break;
      case HttpRequestTypeEnum.delete:
        manageApiObservable = this.dataService.delete(
          manageWearerUrl,
          wearerData
        );
        break;
    }
    let temp = [manageApiObservable];
    if (this.select.bedSlot && this.select.bedSlot.hasOwnProperty('bedslotId')) {
      const roomId = this.select.hasOwnProperty('roomId') ? this.select.roomId : this.wearerDetails?.rooms?.[0]?.id;
      temp.push(this.wearerService.assignBedSlotToResident(this.wearerId, this.select.bedSlot.bedslotId, { roomId, pccBedDesc: this.select.bedSlot.slotName || null }));
    }
    await this.uiUtilityService.showLoader();
    forkJoin(temp).subscribe((response: any) => {
      let success = false;
      for (let i = 0; i < temp.length; i++) {
        if (response[i]?.success) {
          success = true;
        } else {
          success = false;
        }
      }
      if (success) {
        let successMsg = '';
        if (operationType === HttpRequestTypeEnum.post) {
          successMsg = 'Wearer added.';
        } else if (operationType === HttpRequestTypeEnum.put) {
          successMsg = 'Wearer details updated.';
        } else if (operationType === HttpRequestTypeEnum.delete) {
          successMsg = 'Wearer deleted.';
        }
        this.uiUtilityService.showToast(successMsg, TOAST.success);
        if (operationType === HttpRequestTypeEnum.delete) {
          this.onNavBack();
          return;
        }
        if (this.roomSwitchTriggered) {
          this.goToSelectRoomPage();
        } else if (!this.wearerId && response.data?.id) {
          this.wearerDetailsForm?.markAsPristine();
          this.wearerId = response.data.id;
          // this.isEditEnable = true;
          this.ionViewWillEnter();
        } else {
          this.navigateBack();
        }
      } else {
        const header = 'Error';
        this.uiUtilityService.showAlert(
          response?.errorMessage as string,
          header
        );
        this.wearerService.activeWearerDetails = new WearerDetailsModel();
      }
      this.cdRef.detectChanges();
    });
  };

  async manageWearer(operationType: string) {
    if (operationType === HttpRequestTypeEnum.put && this.f['roomName']?.value && !this.f['slotName']?.value) {
      this.uiUtilityService.showAlert('Select a bed slot or unassign from the room.', 'Bed Slot Not Selected');
      return;
    }
    const wearerName = this.uiUtilityService.getEntityDisplayName(this.wearerDetails?.residentDetail);
    if (operationType === HttpRequestTypeEnum.put && this.select.bedSlot?.residentId && this.select.bedSlot?.residentId !== this.wearerId) {
      const message =
        `Are you sure you want to assign ${wearerName} to ${this.f['roomName']?.value}-${this.select.bedSlot.slotName}? ${this.select.bedSlot.wearerName} will be unassigned from the room.`;
      const header = 'Confirm';
      this.uiUtilityService.presentConfirm(message, () => this.didManageWearer(operationType), undefined, header, 'Ok');
    } else {
      this.didManageWearer(operationType);
    }
  }

  navigateBack() {
    this.wearerService.activeWearerDetails = new WearerDetailsModel();
    this.nav.pop();
  }

  stopGoingBack() {
    if (this.wearerDetailsForm && !this.wearerDetailsForm.pristine) {
    } else {
      this.nav.pop();
    }
  }

  backClick() {
    if (this.wearerDetailsForm && !this.wearerDetailsForm.pristine) {
      this.backButtonClicked = true;
      const header = 'Confirm';
      const message =
        'It looks like you have been editing something. ' +
        'If you leave before saving, your changes will be lost.';
      this.showAlert(header, message);
    } else {
      this.navigateBack();
    }
  }

  deleteWearer() {
    const header = 'Delete This Wearer';
    const message =
      'You are about to delete this wearer. This action cannot be undone.';
    const buttons: AlertButtonModel[] = [
      {
        text: 'cancel',
      },
      {
        text: 'Delete',
        handler: () => {
          this.manageWearer(HttpRequestTypeEnum.delete);
        },
      },
    ];
    this.uiUtilityService.showAlert(message, header, buttons);
  }

  assetDefaultLocationOn() {
    if (this.isAsset) {
      this.wearerDetailsForm?.get('location')?.setValue(true);
    }
    //  else {
    //   this.wearerDetailsForm.get('location').setValue(this.wearerDetails.location);
    // }
  }

  setEditMode(mode: boolean) {
    // this.isEditEnable = mode;
    // this.wearerDetailsInputConfig.map(
    //   (config) => (config.detail = this.isEditEnable)
    // );
    this.nav.push(WearerDetailsPage, { id: this.wearerId, mode: 'edit' });
  }

  getConfig(config: UnifiedInput) {
    return { ...config };
  }

  get isReadMode() {
    return !!this.wearerId && !this.isEditEnable;
  }

  get isResident() {
    const selected = this.getControl('wearerGroupId')?.value;
    const wearerGroupName = this.storedGroups.find((item: any) => item.id === selected)?.wearerGroupName;
    return wearerGroupName?.toLowerCase()?.indexOf('resident') !== -1;
  }


get hasValidEtectId(): boolean {
  if (!this.wearerDetailsForm) {
    return false;
  }

  // Use array syntax ['etectDetails.etectId'] to target flat keys with literal dots
  const id = this.wearerDetailsForm.get(['etectDetails.etectId'])?.value;
  console.log("Resolved etect ID value:", id);

  return id !== null && id !== undefined && id !== '';
}

//get f() {
//  return (path: string) => this.wearerDetailsForm?.get(path);
//}
 
  get f() { return this.wearerDetailsForm?.controls || {}; }

  onAlertToggle(ev: any, key: string) {
    const val = ev.detail.checked;
  }

  handleAlertChange(event: any) {
    // this.bedData = {};
    // const bed = this.beds.find((item: any) => item.id === event.detail.value);
    // if (!this.isBedError(bed)) {
    //   this.getBedPosition(bed);
    // } else {
    //   this.bedData = bed;
    // }
  }

  onInfo(ev: any, msg: string) {
    ev.stopPropagation();
    this.uiUtilityService.showAlert(msg, 'Info');
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.alertThresholdOption = null;
  }

  async showSelectModal({ title, emptyMessage, key, displayName, value, list, isSave, header, rightSlot }: any) {
    const modal = await this.modalCtrl.create({
      component: UnifiedSelectComponent,
      componentProps: { data: { title, emptyMessage, key, displayName, value, list, header, rightSlot }, isSave }
    });
    modal.present();
    return await modal.onWillDismiss();
  };

  async didSelectBedSlotModal(bedSlots: any[]) {
    const { data, role } = await this.showSelectModal({ title: 'Select Bed Slot', emptyMessage: 'No bed slots available right now.', key: 'bedslotId', displayName: 'slotName', value: this.wearerDetails?.residentDetail?.bedslotId || '', list: bedSlots, isSave: false, header: this.f['roomName']?.value, rightSlot: { title: 'wearerName', titleFallback: 'Unassigned' } });
    if (role === 'confirm') {
      let slot = null;
      if (data !== '') {
        const bedSlot = bedSlots.find((item: any) => item.bedslotId === data);
        if (bedSlot) {
          this.select['bedSlot'] = bedSlot;
          slot = bedSlot.slotName;
        }
      } else {
        this.select['bedSlot'] = {
          bedslotId: null
        };
      }
      this.wearerDetailsForm?.controls['slotName'].patchValue(slot);
      this.wearerDetailsForm?.markAsDirty();
      this.wearerDetailsForm?.updateValueAndValidity();
      this.cdRef.detectChanges();
    }
  }

  async setBedSlot() {
    const roomN = this.select.hasOwnProperty('roomId') ? this.select.roomId : this.wearerDetails?.rooms?.[0]?.id;
    if (!roomN) {
      return;
    }
    await this.uiUtilityService.showLoader();
    this.settingsService.getBedRoomSlotListForPatient(roomN).subscribe((response: any) => {
      if (response && response.success && response.data) {
        const none = {
          bedslotId: '',
          slotName: 'None'
        };
        response.data.forEach((item: any) => {
          item.wearerName = this.uiUtilityService.getEntityShortDisplayName(item);
        });
        const pccOffMaxmBedSlotPerRoom = this.headerService.getCurrentFacility.pccOffMaxmBedSlotPerRoom;
        const bedSlotsList = response.data.slice(0, pccOffMaxmBedSlotPerRoom);
        const bedSlots = bedSlotsList?.length ? [none, ...bedSlotsList] : [];
        this.didSelectBedSlotModal(bedSlots);
      } else {
        const header = 'Error';
        this.uiUtilityService.showAlert(response.errorMessage!, header);
      }
    });
  }

  async didSelectRoomModal() {
    const roomId = this.select.hasOwnProperty('roomId') ? this.select.roomId : this.wearerDetails?.rooms?.[0]?.id;
    const { data, role } = await this.showSelectModal({ title: 'Select Room', emptyMessage: 'No rooms available right now.', key: 'id', displayName: 'roomName', value: roomId || '', list: this.rooms, isSave: false });
    if (role === 'confirm') {
      if (data !== '') {
        const room = this.rooms.find((item: any) => item.id === data);
        if (room) {
          this.select['roomId'] = room.id;
          this.select['roomName'] = room.roomName;
          this.select['bedSlot'] = {
            bedslotId: null
          };
          // this.assignRoomToBed(room);
          this.wearerDetailsForm?.controls['roomName'].patchValue(room.roomName);
          this.wearerDetailsForm?.controls['slotName'].patchValue('');
          this.wearerDetailsForm?.updateValueAndValidity();
          this.cdRef.detectChanges();
        }
      } else {
        // none
        this.select['roomId'] = '';
        this.select['roomName'] = '';
        this.select['bedSlot'] = {
          bedslotId: null
        };
        this.wearerDetailsForm?.controls['roomName'].patchValue(null);
        this.wearerDetailsForm?.controls['slotName'].patchValue('');
        this.wearerDetailsForm?.updateValueAndValidity();
        this.cdRef.detectChanges();
      }
    }
  }

  async setRoom() {
    if (this.rooms.length) {
      this.didSelectRoomModal();
    } else {
      await this.uiUtilityService.showLoader();
      this.settingsService.getBedRoomList().subscribe((response: any) => {
        if (response && response.success && response.data) {
          const none = {
            id: '',
            roomName: 'None'
          };
          this.rooms = response.data?.length ? [none, ...response.data] : [none];
          this.didSelectRoomModal();
        } else {
          const header = 'Error';
          this.uiUtilityService.showAlert(response.errorMessage!, header);
        }
      });
    }
  }
}
